/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect } from "react";
import { View, Animated } from "react-native";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import { ProfileImage, Text } from "UpduoLibrary";
import {
  skaleBlack,
  skaleDeactiveGray,
  skaleWhite,
  upduoPrimaryBlue,
  upduoDarkGreen,
  upduoSecondLightestGray,
  upduoSummitGradientGray,
} from "config/UpduoColors";

interface SessionPartnerStatusProps {
  partnerObject: any;
}

export default function SessionPartnerStatus({
  partnerObject,
}: SessionPartnerStatusProps) {
  const isPartnerChatReady = useSelector(
    (state: RootState) => state.isPartnerChatReady
  );

  const partnerName = `${partnerObject.firstName} ${partnerObject.lastName}`;

  const animation = new Animated.Value(0);

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(animation, {
          toValue: 1,
          duration: 600,
          useNativeDriver: false,
        }),
        Animated.timing(animation, {
          toValue: 0,
          duration: 600,
          useNativeDriver: false,
        }),
      ])
    ).start();
  }, []);

  const textInterpolation = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [upduoSummitGradientGray, skaleDeactiveGray],
  });

  const textAnimatedStyle = {
    color: textInterpolation,
  };

  return (
    <View>
      <View
        style={{
          position: "absolute",
          top: -50,
          right: 0,
        }}
      >
        {isPartnerChatReady ? (
          <Text
            style={{
              color: upduoDarkGreen,
              fontSize: 16,
              fontWeight: "500",
            }}
          >
            {partnerObject?.firstName} {"is ready!"}
          </Text>
        ) : (
          <Animated.Text
            style={[
              textAnimatedStyle,
              {
                fontSize: 16,
                fontFamily: "ObjectSans-Regular",
                fontWeight: "500",
                lineHeight: 16 * 1.4,
              },
            ]}
          >
            {partnerObject?.firstName} {"is connecting..."}
          </Animated.Text>
        )}
      </View>

      <View
        style={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          width: "100%",
          backgroundColor: skaleWhite,
          borderColor: upduoSecondLightestGray,
          borderWidth: 1,
          borderRadius: 20,
          padding: 40,
        }}
      >
        <ProfileImage
          userObject={partnerObject}
          size={100}
          round={false}
          borderRadius={8}
        />
        <View
          style={{
            borderRadius: 4,
            paddingHorizontal: 20,
            flexDirection: "column",
            width: "70%",
          }}
        >
          <Text
            isHeading={true}
            style={{
              fontWeight: "800",
              color: upduoPrimaryBlue,
              fontSize: 20,
              marginBottom: 2,
            }}
            numberOfLines={1}
            ellipsizeMode={"tail"}
          >
            {partnerName}
          </Text>
          {partnerObject?.district?.name ? (
            <Text
              style={{ color: skaleBlack, fontSize: 16, marginBottom: 2 }}
              numberOfLines={1}
              ellipsizeMode={"tail"}
            >
              {partnerObject?.district?.name}
            </Text>
          ) : null}
          {partnerObject?.title ? (
            <Text
              style={{ color: skaleBlack, fontSize: 16, marginBottom: 2 }}
              numberOfLines={1}
              ellipsizeMode={"tail"}
            >
              {partnerObject?.title}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );
}
